/**
 * PlayerCardsRenderer — floating cards HUD, bottom-center.
 *
 * Two per-player cards showing damage % and shot tier pips.
 * Gap between cards holds turn label, timer, and wind indicator.
 * Cards breathe and flash on hit. Both cards always at full opacity —
 * turn indication is done by dimming tanks in the world, not HUD cards.
 * Chrome color tints to the active stage theme.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { PLAYER_CARD, PLAYER_COLORS, WORLD, Z, URGENCY, WEAPON_CHARGE } from '../../constants';
import { getActiveTheme } from '../../stages/registry';
import { modeUsesShotTiers, modeUsesHP, modeUsesStocks } from '../../game/mode';
import { getEffectiveShots, lookupTank } from '../../tanks';
import { CLASSIC } from '../../constants';
import type { GameState } from '../../types';
import type { ShotTierDef } from '../../tanks/types';
import { getChargeFraction, isChargeReady } from '../../game/weapon-charge';

const C = PLAYER_CARD;

export class PlayerCardsRenderer {
  private container: Container;

  // Per-card Graphics/Text (index = player index)
  private cardBgs: Graphics[];
  private cardBorders: Graphics[];
  private accentLines: Graphics[];
  private playerLabels: Text[];
  private tankNameLabels: Text[];
  private damageTexts: Text[];
  private pipGfx: Graphics[];

  // Gap elements
  private timerLabel: Text;

  // Charge bar (between cards, during charge phase)
  private chargeBarBg: Graphics;
  private chargeBarFill: Graphics;
  private chargeBarAlpha: number = 0;

  // Stock pips
  private stockGfx: Graphics[];

  // Per-card overlays
  private specialGfx: Graphics[];
  private weaponLabels: Text[];

  // Weapon charge bar
  private chargeMeterBgs: Graphics[];
  private chargeMeterFills: Graphics[];
  private chargeReadyLabels: Text[];

  // Trick score
  private scoreTexts: Text[];
  private prevScores: number[] = [0, 0];
  private scoreScale: number[] = [1, 1];

  // Score flydown animation
  private flydownActive: boolean[] = [false, false];
  private flydownDelta: number[] = [0, 0];
  private flydownT: number[] = [0, 0];        // 0→1 progress
  private flydownTexts: Text[];
  private displayedScores: number[] = [0, 0];  // smoothly counting up

  // Animation state per player
  private cardGlow: number[] = [0, 0];
  private dmgScale: number[] = [1, 1];
  private dmgShake: number[] = [0, 0];
  private prevDamage: number[] = [0, 0];
  private prevHp: number[];
  private breatheTime: number = 0;

  // Timer state (driven by external calls, not polling)
  private timerSeconds: number = -1;
  private timerVisible: boolean = false;

  constructor(uiContainer: Container) {
    this.container = new Container();
    this.container.zIndex = Z.HUD_BASE;
    this.container.visible = false;
    uiContainer.addChild(this.container);

    this.cardBgs = [];
    this.cardBorders = [];
    this.accentLines = [];
    this.playerLabels = [];
    this.tankNameLabels = [];
    this.damageTexts = [];
    this.pipGfx = [];
    this.prevHp = [CLASSIC.MAX_HP, CLASSIC.MAX_HP];

    for (let i = 0; i < 2; i++) {
      const bg = new Graphics();
      this.container.addChild(bg);
      this.cardBgs.push(bg);

      const border = new Graphics();
      this.container.addChild(border);
      this.cardBorders.push(border);

      const accent = new Graphics();
      this.container.addChild(accent);
      this.accentLines.push(accent);

      const pips = new Graphics();
      this.container.addChild(pips);
      this.pipGfx.push(pips);

      const label = new Text({
        text: `P${i + 1}`,
        style: new TextStyle({
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: C.LABEL_FONT_SIZE,
          fontWeight: 'bold',
          fill: PLAYER_COLORS[i],
          stroke: { color: 0x000000, width: 2 },
        }),
      });
      this.container.addChild(label);
      this.playerLabels.push(label);

      const tankName = new Text({
        text: '',
        style: new TextStyle({
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: C.TANK_NAME_FONT_SIZE,
          fontWeight: 'bold',
          fill: 0xaaaaaa,
          stroke: { color: 0x000000, width: 2 },
        }),
      });
      this.container.addChild(tankName);
      this.tankNameLabels.push(tankName);

      const dmg = new Text({
        text: '0%',
        style: new TextStyle({
          fontFamily: '"Arial Black", "Impact", sans-serif',
          fontSize: C.DAMAGE_FONT_SIZE,
          fontWeight: 'bold',
          fill: 0xffffff,
          stroke: { color: PLAYER_COLORS[i], width: 1 },
        }),
      });
      dmg.anchor.set(0.5, 0.5);
      this.container.addChild(dmg);
      this.damageTexts.push(dmg);
    }

    // Stock pips
    this.stockGfx = [];
    for (let i = 0; i < 2; i++) {
      const gfx = new Graphics();
      this.container.addChild(gfx);
      this.stockGfx.push(gfx);
    }

    // Charge bar (between cards)
    this.chargeBarBg = new Graphics();
    this.container.addChild(this.chargeBarBg);
    this.chargeBarFill = new Graphics();
    this.container.addChild(this.chargeBarFill);

    // Gap — timer
    this.timerLabel = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: C.TIMER_FONT_SIZE,
        fontWeight: 'bold',
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    this.timerLabel.anchor.set(0.5, 0);
    this.timerLabel.visible = false;
    this.container.addChild(this.timerLabel);

    // Per-card: special availability glow
    this.specialGfx = [];
    for (let i = 0; i < 2; i++) {
      const gfx = new Graphics();
      this.container.addChild(gfx);
      this.specialGfx.push(gfx);
    }

    // Per-card: weapon name label
    this.weaponLabels = [];
    for (let i = 0; i < 2; i++) {
      const wl = new Text({
        text: '',
        style: new TextStyle({
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 11,
          fontWeight: 'bold',
          fill: 0xffffff,
          stroke: { color: 0x000000, width: 2 },
        }),
      });
      wl.anchor.set(0.5, 0);
      wl.visible = false;
      this.container.addChild(wl);
      this.weaponLabels.push(wl);
    }

    // Per-card: weapon charge bar
    this.chargeMeterBgs = [];
    this.chargeMeterFills = [];
    this.chargeReadyLabels = [];
    for (let i = 0; i < 2; i++) {
      const bg = new Graphics();
      this.container.addChild(bg);
      this.chargeMeterBgs.push(bg);

      const fill = new Graphics();
      this.container.addChild(fill);
      this.chargeMeterFills.push(fill);

      const label = new Text({
        text: '[2] CHARGED',
        style: new TextStyle({
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 9,
          fontWeight: 'bold',
          fill: WEAPON_CHARGE.COLOR_FULL,
          stroke: { color: 0x000000, width: 2 },
        }),
      });
      label.anchor.set(0.5, 1);
      label.visible = false;
      this.container.addChild(label);
      this.chargeReadyLabels.push(label);
    }

    // Per-card: trick score (below card)
    this.scoreTexts = [];
    for (let i = 0; i < 2; i++) {
      const st = new Text({
        text: '',
        style: new TextStyle({
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 11,
          fontWeight: 'bold',
          fill: 0xffcc00,
          stroke: { color: 0x000000, width: 2 },
        }),
      });
      st.anchor.set(0.5, 0);
      st.visible = false;
      this.container.addChild(st);
      this.scoreTexts.push(st);
    }

    // Per-card: flydown score text (animated number that flies from center to score)
    this.flydownTexts = [];
    for (let i = 0; i < 2; i++) {
      const ft = new Text({
        text: '',
        style: new TextStyle({
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 16,
          fontWeight: 'bold',
          fill: 0xffcc00,
          stroke: { color: 0x000000, width: 3 },
        }),
      });
      ft.anchor.set(0.5, 0.5);
      ft.visible = false;
      this.container.addChild(ft);
      this.flydownTexts.push(ft);
    }
  }

  /** Called by HUDRenderer when turn timer updates. */
  updateTurnTimer(secondsLeft: number): void {
    this.timerSeconds = secondsLeft;
    this.timerVisible = true;
  }

  /** Called by HUDRenderer when turn timer should hide. */
  hideTurnTimer(): void {
    this.timerVisible = false;
  }

  hide(): void {
    this.container.visible = false;
  }

  update(state: GameState, dt: number): void {
    this.container.visible = true;
    this.breatheTime += dt;

    const chrome = getActiveTheme(state.stageId).bumperBright;
    const cw = WORLD.CANVAS_WIDTH;
    const ch = WORLD.CANVAS_HEIGHT;

    // Card layout
    const totalW = C.CARD_WIDTH * 2 + C.CARD_GAP;
    const startX = (cw - totalW) / 2;
    const cardY = ch - C.CARD_HEIGHT - C.CARD_Y_BOTTOM;

    // Determine active player
    const isDefense = state.phase === 'defensive_movement' && state.defenseActivePlayer >= 0;
    const activeIdx = isDefense ? state.defenseActivePlayer : state.currentPlayer;

    const isHP = modeUsesHP(state.modeState.mode);
    const showShotTiers = modeUsesShotTiers(state.modeState.mode);

    for (let i = 0; i < 2; i++) {
      if (i >= state.players.length) {
        this.cardBgs[i].visible = false;
        this.cardBorders[i].visible = false;
        this.accentLines[i].visible = false;
        this.playerLabels[i].visible = false;
        this.tankNameLabels[i].visible = false;
        this.damageTexts[i].visible = false;
        this.pipGfx[i].visible = false;
        continue;
      }

      const p = state.players[i];
      const isActive = i === activeIdx;
      const pColor = PLAYER_COLORS[i];

      // Card X position
      const cx = i === 0 ? startX : startX + C.CARD_WIDTH + C.CARD_GAP;

      // Breathe animation — subtle Y oscillation
      const breatheOffset = Math.sin(this.breatheTime * C.BREATHE_SPEED + i * Math.PI) * C.BREATHE_AMPLITUDE;
      const cy = cardY + breatheOffset;

      // Damage change detection
      if (isHP) {
        if (p.hp < this.prevHp[i] - 0.01) {
          this.cardGlow[i] = 1;
          this.dmgShake[i] = 1;
        }
        this.prevHp[i] = p.hp;
      } else {
        if (p.damagePercent > this.prevDamage[i] + 0.01) {
          this.cardGlow[i] = 1;
          this.dmgShake[i] = 1;
        }
        this.prevDamage[i] = p.damagePercent;
      }

      // Decay animations
      if (this.cardGlow[i] > 0) this.cardGlow[i] = Math.max(0, this.cardGlow[i] - dt * C.GLOW_DECAY_RATE);
      if (this.dmgShake[i] > 0) this.dmgShake[i] = Math.max(0, this.dmgShake[i] - dt * C.SHAKE_DECAY_RATE);

      // Scale punch: 1.45x → 1x
      const scalePunch = 1 + (C.DAMAGE_SCALE_PUNCH - 1) * this.cardGlow[i];
      this.dmgScale[i] = this.dmgScale[i] + (scalePunch - this.dmgScale[i]) * Math.min(1, dt * C.SCALE_SETTLE_RATE);

      // ── Card background ──
      const bg = this.cardBgs[i];
      bg.visible = true;
      bg.clear();
      bg.roundRect(cx, cy, C.CARD_WIDTH, C.CARD_HEIGHT, C.CARD_CORNER_RADIUS);
      bg.fill({ color: 0x0a0a19, alpha: C.ACTIVE_BG_ALPHA });

      // Hit flash overlay
      if (this.cardGlow[i] > 0.01) {
        bg.roundRect(cx, cy, C.CARD_WIDTH, C.CARD_HEIGHT, C.CARD_CORNER_RADIUS);
        bg.fill({ color: pColor, alpha: 0.15 * this.cardGlow[i] });
      }

      // ── Card border ──
      const border = this.cardBorders[i];
      border.visible = true;
      border.clear();
      if (isActive) {
        border.roundRect(cx, cy, C.CARD_WIDTH, C.CARD_HEIGHT, C.CARD_CORNER_RADIUS);
        border.stroke({ color: pColor, width: C.ACTIVE_BORDER_WIDTH, alpha: 0.9 });
      } else {
        border.roundRect(cx, cy, C.CARD_WIDTH, C.CARD_HEIGHT, C.CARD_CORNER_RADIUS);
        border.stroke({ color: chrome, width: C.INACTIVE_BORDER_WIDTH, alpha: 0.4 });
      }

      // ── Accent line (top of card) ──
      const accent = this.accentLines[i];
      accent.visible = true;
      accent.clear();
      const accentAlpha = isActive ? 0.8 : 0.3;
      const accentInset = C.CARD_CORNER_RADIUS;
      accent.moveTo(cx + accentInset, cy + 1);
      accent.lineTo(cx + C.CARD_WIDTH - accentInset, cy + 1);
      accent.stroke({ color: pColor, width: 2, alpha: accentAlpha });

      // ── Player label ──
      const label = this.playerLabels[i];
      label.visible = true;
      label.text = p.name;
      label.position.set(cx + C.LABEL_X, cy + C.LABEL_Y);

      // ── Tank name ──
      const tankNameLabel = this.tankNameLabels[i];
      tankNameLabel.visible = true;
      const tankDef = lookupTank(p.tankId);
      tankNameLabel.text = tankDef.name.toUpperCase();
      tankNameLabel.style.fill = isActive ? 0xcccccc : 0x777777;
      tankNameLabel.position.set(cx + C.TANK_NAME_X, cy + C.TANK_NAME_Y);

      // ── Shot tier pips (top-right) ──
      const pips = this.pipGfx[i];
      pips.visible = true;
      pips.clear();
      if (showShotTiers && p.alive) {
        const shots = getEffectiveShots(p.tankId, p.terraformerMode);
        const pipCount = Math.min(shots.length, 3);
        const pipStartX = cx + C.CARD_WIDTH - C.PIP_RIGHT_MARGIN - (pipCount - 1) * C.PIP_SPACING;
        for (let t = 0; t < pipCount; t++) {
          const px = pipStartX + t * C.PIP_SPACING;
          const py = cy + C.PIP_Y;
          const isSelectedPip = p.selectedShotTier === (t + 1);
          if (isSelectedPip) {
            pips.circle(px, py, C.PIP_RADIUS);
            pips.fill({ color: chrome, alpha: 1 });
            // Glow ring
            pips.circle(px, py, C.PIP_RADIUS + 2);
            pips.stroke({ color: chrome, width: 1, alpha: 0.5 });
          } else {
            pips.circle(px, py, C.PIP_RADIUS);
            pips.fill({ color: 0x333333, alpha: 0.8 });
          }
        }
      }

      // ── Stock pips (below player label) ──
      const stockGfx = this.stockGfx[i];
      stockGfx.clear();
      if (modeUsesStocks(state.modeState.mode)) {
        stockGfx.visible = true;
        const maxStocks = 2;
        const stockPipR = 4;
        const stockPipGap = 12;
        const stockStartX = cx + C.LABEL_X;
        const stockPipY = cy + C.LABEL_Y + C.LABEL_FONT_SIZE + 4;
        for (let s = 0; s < maxStocks; s++) {
          const spx = stockStartX + s * stockPipGap;
          if (s < p.stocks) {
            // Filled pip
            stockGfx.circle(spx, stockPipY, stockPipR);
            stockGfx.fill({ color: pColor, alpha: 0.9 });
          } else {
            // Empty pip
            stockGfx.circle(spx, stockPipY, stockPipR);
            stockGfx.stroke({ color: pColor, width: 1, alpha: 0.3 });
          }
        }
      } else {
        stockGfx.visible = false;
      }

      // ── Damage text ──
      const dmg = this.damageTexts[i];
      dmg.visible = true;

      let dmgStr: string;
      if (!p.alive) {
        dmgStr = 'KO';
      } else if (isHP) {
        dmgStr = `${Math.ceil(p.hp)}`;
      } else {
        dmgStr = `${Math.round(p.damagePercent)}%`;
      }
      dmg.text = dmgStr;
      dmg.style.stroke = { color: pColor, width: 1 };

      // Shake offset
      const shakeX = this.dmgShake[i] > 0.01
        ? (Math.random() - 0.5) * C.DAMAGE_SHAKE_MAX * this.dmgShake[i]
        : 0;
      dmg.position.set(cx + C.CARD_WIDTH / 2 + shakeX, cy + 50);
      dmg.scale.set(this.dmgScale[i]);
    }

    // ── Per-card overlays (special glow + weapon label) ──
    const isAiming = state.phase === 'aiming';
    for (let i = 0; i < 2; i++) {
      const sgfx = this.specialGfx[i];
      sgfx.clear();
      const wl = this.weaponLabels[i];

      if (i >= state.players.length || !state.players[i].alive) {
        sgfx.visible = false;
        wl.visible = false;
        continue;
      }

      const p = state.players[i];
      const cx = i === 0 ? startX : startX + C.CARD_WIDTH + C.CARD_GAP;
      const breatheOffset = Math.sin(this.breatheTime * C.BREATHE_SPEED + i * Math.PI) * C.BREATHE_AMPLITUDE;
      const cy = cardY + breatheOffset;

      // Special availability glow (bottom-left of card)
      if (isAiming && !isDefense) {
        sgfx.visible = true;
        const sx = cx + 16;
        const sy = cy + C.CARD_HEIGHT - 16;
        if (!p.specialUsed) {
          const pulse = 0.5 + Math.sin(Date.now() * 0.004) * 0.4;
          sgfx.circle(sx, sy, 6);
          sgfx.fill({ color: chrome, alpha: pulse });
          // Outer glow ring
          sgfx.circle(sx, sy, 9);
          sgfx.stroke({ color: chrome, width: 1, alpha: pulse * 0.4 });
        } else {
          sgfx.circle(sx, sy, 6);
          sgfx.fill({ color: 0x333333, alpha: 0.2 });
        }
      } else {
        sgfx.visible = false;
      }

      // Weapon name label (bottom-center of card)
      if (isAiming && showShotTiers && !isDefense) {
        const shots = getEffectiveShots(p.tankId, p.terraformerMode);
        const tierIdx = p.selectedShotTier - 1;
        if (tierIdx >= 0 && tierIdx < shots.length) {
          const shot: ShotTierDef = shots[tierIdx];
          wl.visible = true;
          wl.text = shot.label.toUpperCase();
          wl.style.fill = shot.tierColor;
          wl.position.set(cx + C.CARD_WIDTH / 2, cy + 72);
        } else {
          wl.visible = false;
        }
      } else {
        wl.visible = false;
      }

      // Weapon charge bar (bottom of card)
      const chargeBg = this.chargeMeterBgs[i];
      const chargeFill = this.chargeMeterFills[i];
      const chargeLabel = this.chargeReadyLabels[i];

      if (showShotTiers && p.alive) {
        chargeBg.visible = true;
        chargeFill.visible = true;
        chargeBg.clear();
        chargeFill.clear();

        const barX = cx + WEAPON_CHARGE.BAR_X_INSET;
        const barY = cy + C.CARD_HEIGHT - 12;
        const barW = C.CARD_WIDTH - 2 * WEAPON_CHARGE.BAR_X_INSET;
        const barH = WEAPON_CHARGE.BAR_HEIGHT;

        // Background track
        chargeBg.roundRect(barX, barY, barW, barH, 2);
        chargeBg.fill({ color: WEAPON_CHARGE.COLOR_EMPTY, alpha: 0.4 });

        // Fill
        const frac = getChargeFraction(p);
        if (frac > 0) {
          const fillW = barW * frac;
          let fillColor: number;
          let fillAlpha = 1;
          if (frac >= 1) {
            fillColor = WEAPON_CHARGE.COLOR_FULL;
            fillAlpha = WEAPON_CHARGE.PULSE_MIN_ALPHA + (1 - WEAPON_CHARGE.PULSE_MIN_ALPHA) * (0.5 + Math.sin(Date.now() * WEAPON_CHARGE.PULSE_SPEED) * 0.5);
          } else if (frac >= 0.5) {
            fillColor = WEAPON_CHARGE.COLOR_HIGH;
          } else {
            fillColor = WEAPON_CHARGE.COLOR_CHARGING;
          }
          chargeFill.roundRect(barX, barY, fillW, barH, 2);
          chargeFill.fill({ color: fillColor, alpha: fillAlpha });
        }

        // "[2] CHARGED" label — show when charge is full and player hasn't swapped to S2/S3
        if (isChargeReady(p) && p.selectedShotTier === 1) {
          chargeLabel.visible = true;
          chargeLabel.text = '[2] CHARGED';
          chargeLabel.position.set(cx + C.CARD_WIDTH / 2, barY - 2);
          // Hide weapon label to prevent overlap
          wl.visible = false;
        } else {
          chargeLabel.visible = false;
        }
      } else {
        chargeBg.visible = false;
        chargeFill.visible = false;
        chargeLabel.visible = false;
      }

      // Trick score (below card, centered)
      const scoreTx = this.scoreTexts[i];
      const trickScore = state.trickScores?.[i] ?? 0;
      const scoreTargetX = cx + C.CARD_WIDTH / 2;
      const scoreTargetY = cy + C.CARD_HEIGHT + 10;

      // Detect score change → start flydown
      if (trickScore > this.prevScores[i] && this.prevScores[i] >= 0) {
        const delta = trickScore - this.prevScores[i];
        if (delta > 0) {
          this.flydownActive[i] = true;
          this.flydownDelta[i] = delta;
          this.flydownT[i] = 0;
          this.flydownTexts[i].text = `+${delta.toLocaleString()}`;
          this.flydownTexts[i].visible = true;
          this.flydownTexts[i].alpha = 1;
          this.displayedScores[i] = this.prevScores[i];
        }
      }
      this.prevScores[i] = trickScore;

      // Flydown animation
      const flyTx = this.flydownTexts[i];
      if (this.flydownActive[i]) {
        this.flydownT[i] += dt * 1.5; // ~0.67 seconds total
        const t = Math.min(1, this.flydownT[i]);
        const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic

        // Fly from above center to score position
        const startY = scoreTargetY - 80;
        const flyY = startY + (scoreTargetY - startY) * eased;
        flyTx.position.set(scoreTargetX, flyY);
        flyTx.alpha = 1;
        flyTx.scale.set(1.3 - 0.3 * eased); // shrink slightly as it lands

        // Count up displayed score during flight
        const countT = Math.min(1, t * 1.5); // finish counting before landing
        this.displayedScores[i] = Math.round(
          (trickScore - this.flydownDelta[i]) + this.flydownDelta[i] * countT
        );

        if (t >= 1) {
          this.flydownActive[i] = false;
          flyTx.visible = false;
          this.displayedScores[i] = trickScore;
          this.scoreScale[i] = 1.4; // punch on arrival
        }
      } else {
        flyTx.visible = false;
        this.displayedScores[i] = trickScore;
      }

      if (trickScore > 0 && p.alive) {
        scoreTx.visible = true;
        scoreTx.text = `${this.displayedScores[i].toLocaleString()} PTS`;
        scoreTx.position.set(scoreTargetX, scoreTargetY);
        this.scoreScale[i] += (1 - this.scoreScale[i]) * Math.min(1, dt * 8);
        scoreTx.scale.set(this.scoreScale[i]);
      } else {
        scoreTx.visible = false;
      }
    }

    // ── Gap elements (between cards) ──
    const gapCenterX = cw / 2;
    const gapTopY = cardY;

    // Timer
    if (this.timerVisible && this.timerSeconds >= 0) {
      this.timerLabel.visible = true;
      const display = Math.ceil(Math.max(0, this.timerSeconds));
      this.timerLabel.text = `${display}`;

      if (this.timerSeconds <= 10) {
        const pulse = 0.5 + Math.sin(Date.now() * 0.008) * 0.5;
        this.timerLabel.style.fill = URGENCY.CRITICAL_COLOR;
        this.timerLabel.alpha = 0.7 + pulse * 0.3;
      } else {
        this.timerLabel.style.fill = chrome;
        this.timerLabel.alpha = 0.8;
      }
      this.timerLabel.position.set(gapCenterX, gapTopY + 10);
    } else {
      this.timerLabel.visible = false;
    }

    // ── Charge bar (between cards, appears during charge) ──
    this.chargeBarBg.clear();
    this.chargeBarFill.clear();

    const isCharging = state.phase === 'aiming' && state.players[state.currentPlayer]?.charging;
    const targetAlpha = isCharging ? 1 : 0;
    const fadeRate = isCharging ? (1 / C.CHARGE_BAR_FADE_IN) : (1 / C.CHARGE_BAR_FADE_OUT);
    this.chargeBarAlpha += (targetAlpha - this.chargeBarAlpha) * Math.min(1, dt * fadeRate);
    if (this.chargeBarAlpha < 0.01) this.chargeBarAlpha = 0;

    if (this.chargeBarAlpha > 0.01) {
      const cbW = C.CHARGE_BAR_WIDTH;
      const cbH = C.CHARGE_BAR_HEIGHT;
      const cbR = C.CHARGE_BAR_CORNER_RADIUS;
      const cbX = cw / 2 - cbW / 2;
      const cbY = cardY - C.CHARGE_BAR_Y_OFFSET - cbH;

      // Background
      this.chargeBarBg.roundRect(cbX, cbY, cbW, cbH, cbR);
      this.chargeBarBg.fill({ color: 0x0a0a15, alpha: 0.7 * this.chargeBarAlpha });
      this.chargeBarBg.roundRect(cbX, cbY, cbW, cbH, cbR);
      this.chargeBarBg.stroke({ color: C.CHARGE_BAR_BORDER_COLOR, width: 1, alpha: 0.6 * this.chargeBarAlpha });

      // Fill
      const currentPlayer = state.players[state.currentPlayer];
      if (currentPlayer) {
        const power = currentPlayer.power;
        const fillFraction = Math.max(0, Math.min(1, power));
        if (fillFraction > 0) {
          const fillW = (cbW - 4) * fillFraction;
          const fillColor = fillFraction >= C.CHARGE_BAR_HIGH_THRESHOLD
            ? C.CHARGE_BAR_COLOR_HIGH
            : C.CHARGE_BAR_COLOR_NORMAL;
          this.chargeBarFill.roundRect(cbX + 2, cbY + 2, fillW, cbH - 4, Math.max(0, cbR - 2));
          this.chargeBarFill.fill({ color: fillColor, alpha: this.chargeBarAlpha });
        }
      }
    }

  }

  destroy(): void {
    for (const g of this.cardBgs) g.destroy();
    for (const g of this.cardBorders) g.destroy();
    for (const g of this.accentLines) g.destroy();
    for (const t of this.playerLabels) t.destroy();
    for (const t of this.tankNameLabels) t.destroy();
    for (const t of this.damageTexts) t.destroy();
    for (const g of this.pipGfx) g.destroy();
    for (const g of this.stockGfx) g.destroy();
    for (const g of this.specialGfx) g.destroy();
    for (const t of this.weaponLabels) t.destroy();
    for (const g of this.chargeMeterBgs) g.destroy();
    for (const g of this.chargeMeterFills) g.destroy();
    for (const t of this.chargeReadyLabels) t.destroy();
    for (const t of this.scoreTexts) t.destroy();
    for (const t of this.flydownTexts) t.destroy();
    this.chargeBarBg.destroy();
    this.chargeBarFill.destroy();
    this.timerLabel.destroy();
    this.container.destroy();
  }
}
